import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MRT_ColumnFiltersState } from 'material-react-table';
import { Box, Tooltip } from '@mui/material';
import { ArrowCircleRightOutlined } from '@mui/icons-material';

import {
  TableFilterConfig,
  UseTableFiltersReturn,
  MRTFilterProps,
  FilterToolbarProps,
  ActiveFiltersDisplayProps,
  PaginationIntegration,
} from './types';
import {
  createEmptyFilters,
  getStoredFilters,
  saveFiltersToStorage,
  columnFiltersToObject,
  objectToColumnFilters,
  hasPendingFilterChanges,
  hasActiveFilterValues,
  buildDisplayNameMap,
  buildQueryParams,
  mergeFilters,
} from './helpers';

interface UseTableFiltersOptions<T extends Record<string, any>> {
  config: TableFilterConfig<T>;
  /** Optional pagination integration for resetting page on filter change */
  pagination?: PaginationIntegration;
  /** Callback when filters are applied */
  onFiltersApplied?: (filters: T) => void;
  /** Callback when filters are cleared */
  onFiltersCleared?: () => void;
}

/**
 * Unified hook for managing table filters with MaterialReactTable integration.
 *
 * Features:
 * - localStorage persistence
 * - Column filter integration with MRT
 * - Apply/Clear workflow
 * - Active filters display
 * - Query params generation for API calls
 */
export const useTableFilters = <T extends Record<string, any>>({
  config,
  pagination,
  onFiltersApplied,
  onFiltersCleared,
}: UseTableFiltersOptions<T>): UseTableFiltersReturn<T> => {
  const { localStorageKey, fields, columnIdMap } = config;

  // ---- State ----

  // Applied filters (persisted, sent to API)
  const [filters, setFilters] = useState<T>(() =>
    getStoredFilters<T>(localStorageKey, fields)
  );

  // Pending column filters (not yet applied)
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  // Ref to always hold the latest columnFilters — avoids stale closures in event handlers
  const columnFiltersRef = useRef<MRT_ColumnFiltersState>([]);
  useEffect(() => {
    columnFiltersRef.current = columnFilters;
  }, [columnFilters]);

  // ---- Computed Values ----

  const hasPendingChanges = useMemo(
    () => hasPendingFilterChanges(columnFilters),
    [columnFilters]
  );

  const hasActiveFilters = useMemo(
    () => hasActiveFilterValues(filters),
    [filters]
  );

  const displayNameMap = useMemo(
    () => buildDisplayNameMap(config),
    [config]
  );

  // ---- Effects ----

  // Persist filters to localStorage when they change
  useEffect(() => {
    saveFiltersToStorage(localStorageKey, filters);
  }, [filters, localStorageKey]);

  // Initialize column filters from stored filters on mount
  useEffect(() => {
    const initialColumnFilters = objectToColumnFilters(filters, columnIdMap);
    if (initialColumnFilters.length > 0) {
      // Don't set pending filters on mount, just ensure initial state is correct
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Actions ----

  const setFilter = useCallback((key: keyof T, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || null,
    }));
  }, []);

  const applyFilters = useCallback((newColumnFilters: MRT_ColumnFiltersState) => {
    setFilters(prev => {
      const updates = columnFiltersToObject<T>(newColumnFilters, fields);
      return mergeFilters(prev, updates, fields);
    });

    // Clear pending filters after applying
    setColumnFilters([]);

    // Reset pagination if integrated
    pagination?.onPageChange(1);

    // Callback
    onFiltersApplied?.(filters);
  }, [fields, pagination, onFiltersApplied, filters]);

  const clearFilters = useCallback(() => {
    const emptyFilters = createEmptyFilters<T>(fields);
    setFilters(emptyFilters);
    setColumnFilters([]);

    // Reset pagination if integrated
    pagination?.onPageChange(1);

    // Callback
    onFiltersCleared?.();
  }, [fields, pagination, onFiltersCleared]);

  const clearFilter = useCallback((key: keyof T) => {
    setFilters(prev => ({
      ...prev,
      [key]: null,
    }));

    // Reset pagination if integrated
    pagination?.onPageChange(1);
  }, [pagination]);

  const resetToDefaults = useCallback(() => {
    const defaultFilters = fields.reduce((acc, field) => {
      acc[field.id as keyof T] = field.defaultValue ?? null;
      return acc;
    }, {} as T);
    setFilters(defaultFilters);
    setColumnFilters([]);
  }, [fields]);

  // ---- MRT Integration ----

  const getMRTFilterProps = useCallback((): MRTFilterProps => {
    const handleColumnFiltersChange = (
      updater: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)
    ) => {
      const newFilters = typeof updater === 'function' ? updater(columnFiltersRef.current) : updater;
      setColumnFilters(newFilters);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        applyFilters(columnFiltersRef.current);
      }
    };

    const handleApplyClick = () => {
      applyFilters(columnFiltersRef.current);
    };

    return {
      enableColumnFilters: true,
      manualFiltering: true,
      onColumnFiltersChange: handleColumnFiltersChange,
      muiTableHeadCellFilterTextFieldProps: {
        onKeyDown: handleKeyDown,
        InputProps: {
          endAdornment: (
            <Tooltip title="Press Enter or click to apply filter">
              <Box
                component="span"
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 4px',
                  borderRadius: '4px',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    transform: 'scale(1.1)',
                  }
                }}
                onClick={handleApplyClick}
              >
                <ArrowCircleRightOutlined
                  sx={{
                    fontSize: '20px',
                    color: hasPendingChanges ? 'primary.main' : 'text.secondary'
                  }}
                />
              </Box>
            </Tooltip>
          ),
        },
        inputProps: {
          style: { textOverflow: 'ellipsis' },
        },
        sx: {
          minWidth: 0,
          '& .MuiInputBase-root': {
            minWidth: 0,
          },
          '& .MuiInputBase-input': {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },
      initialState: {
        columnFilters: objectToColumnFilters(filters, columnIdMap),
      },
    };
  }, [applyFilters, columnIdMap, filters, hasPendingChanges]);

  // ---- UI Integration ----

  const getToolbarProps = useCallback((): FilterToolbarProps => ({
    hasPendingFilters: hasPendingChanges,
    hasActiveFilters,
    onApply: () => applyFilters(columnFiltersRef.current),
    onClear: clearFilters,
    disabled: {
      apply: !hasPendingChanges,
      clear: !hasActiveFilters,
    },
  }), [hasPendingChanges, hasActiveFilters, applyFilters, clearFilters]);

  const getActiveFiltersProps = useCallback((): ActiveFiltersDisplayProps<T> => ({
    appliedFilters: filters,
    onClearFilter: clearFilter,
    displayNameMap,
  }), [filters, clearFilter, displayNameMap]);

  // ---- Query Building ----

  const getQueryParams = useCallback(
    (): Partial<T> => buildQueryParams(filters, fields) as Partial<T>,
    [filters, fields]
  );

  return {
    // State
    filters,
    pendingFilters: columnFilters,
    hasPendingChanges,
    hasActiveFilters,

    // Actions
    setFilter,
    applyFilters,
    clearFilters,
    clearFilter,
    resetToDefaults,
    setColumnFilters,

    // MRT Integration
    getMRTFilterProps,

    // UI Integration
    getToolbarProps,
    getActiveFiltersProps,

    // Query Building
    getQueryParams,
  };
};
